import { FileFormatLabels, FileFormats } from '@common/Transpiler/types';
import { Entity } from '@common/types/Entity';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Button, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { downloadAnalysisReport } from 'frontend/core/src/data/apis/api';
import { useState } from 'react';
import styled from 'styled-components';

import { errorHandler } from '../../../../data/util';
import { AnalysisAction } from '../types';
import { useImportState } from '../useImportState';
import { PercentageCard, StyledMessageCard } from './components/UploadPercentageCard';
import { StyledWhiteCard } from './styled';

const UnderlinedText = styled.span`
  text-decoration: underline;
`;

const StyledContainer = styled(Stack)`
  position: relative;
`;

export const AnalyzeGraph = () => {
  const {
    transpileGraphState: {
      clientState: {
        analysisSummary: { percentage, analysisAction, estimationTime, canDownload }
      }
    },
    transpilerState: { transpilerId },
    formDataWithActions: {
      formContext: { getValues }
    }
  } = useImportState();

  const [isLoading, setIsLoading] = useState(false);
  const downloadReport = usePersistentCallback(async () => {
    setIsLoading(true);
    try {
      await downloadAnalysisReport({ transpilerId });
    } catch (error) {
      errorHandler('Please regenerate the analysis report.');
    } finally {
      setIsLoading(false);
    }
  });

  const { source } = getValues() as { source: FileFormats };
  const isError = analysisAction === AnalysisAction.error;
  const isSuccess = analysisAction === AnalysisAction.success;
  const isSql = getValues('language') === BackEndCodeLanguage.sql;

  let subHeading = 'Pre-migration analysis will be done on selected pipelines.';
  if (isError) {
    subHeading = `Analysis failed.`;
  } else if (isSuccess) {
    subHeading = `Analysis done successfully.`;
  }

  let percentageCardMessage = `Analysis in progress. ${estimationTime ? `Estimated time: ${estimationTime}.` : ''}`;
  if (isError) {
    percentageCardMessage = 'Could not generate analysis report.';
  } else if (isSuccess) {
    percentageCardMessage = 'Click the link below to access and download the analysis report.';
  }

  return (
    <Stack
      width='100%'
      height='100%'
      alignY='center'
      padding={`${theme.spaces.x64} ${theme.spaces.x32} ${theme.spaces.x48}`}>
      <StyledWhiteCard>
        <Stack gap={theme.spaces.x10} width='100%' height='100%'>
          <Text level='xl' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
            Analyzing {FileFormatLabels[source]} to {isSql ? 'SQL' : 'Spark'}
          </Text>
          <Text
            level='sm13'
            weight={theme.fontWeight.normal}
            tone={isError ? theme.colors.error700 : theme.colors.gray700}>
            {subHeading}
          </Text>

          <StackItem grow='1'>
            <Stack height='100%' width='100%' gap={theme.spaces.x20} padding={`${theme.spaces.x40} 0 0`}>
              <Text level='md' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                Overall Progress
              </Text>

              <StyledContainer>
                <StyledMessageCard hasError={isError} isSuccess={isSuccess}>
                  <StyledContainer gap={theme.spaces.x20} width='100%'>
                    <PercentageCard
                      entity={Entity.Transpiler}
                      name={percentageCardMessage}
                      isOverAll
                      alwaysShowPercentage
                      percent={percentage}
                      isError={isError}
                      isSuccess={isSuccess}
                    />
                  </StyledContainer>
                </StyledMessageCard>
              </StyledContainer>

              {isSuccess && canDownload && (
                <Stack direction='horizontal' align='center'>
                  <Button
                    onClick={downloadReport}
                    variant='link'
                    size='l'
                    loading={isLoading}
                    data-test-id='analyze-graph-download-analysis-report-button'>
                    <UnderlinedText>Download Analysis Report</UnderlinedText>
                  </Button>
                </Stack>
              )}
            </Stack>
          </StackItem>
        </Stack>
      </StyledWhiteCard>
    </Stack>
  );
};
